/**
 * Template Card Component
 *
 * Individual template card with thumbnail, title, and category badge.
 * Used in TemplateSection for horizontal scrolling rows.
 */

import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface TemplateCardProps {
  id: string;
  name: string;
  badge: string;
  gradient: string;
  thumbnail?: string;
  description?: string;
  onClick: () => void;
  accentColor: 'blue' | 'purple' | 'amber';
  themeStyles: {
    surface: string;
    border: string;
    text: string;
    textDim: string;
  };
}

// ============================================================================
// Accent Color Mappings
// ============================================================================

const ACCENT_COLORS = {
  blue: {
    hoverBorder: '#3b82f6',
    badgeBg: 'rgba(59, 130, 246, 0.15)',
    badgeText: '#60a5fa',
  },
  purple: {
    hoverBorder: '#a855f7',
    badgeBg: 'rgba(168, 85, 247, 0.15)',
    badgeText: '#c084fc',
  },
  amber: {
    hoverBorder: '#f59e0b',
    badgeBg: 'rgba(245, 158, 11, 0.15)',
    badgeText: '#fbbf24',
  },
};

// ============================================================================
// Component
// ============================================================================

export function TemplateCard({
  name,
  badge,
  gradient,
  thumbnail,
  description,
  onClick,
  accentColor,
  themeStyles,
}: TemplateCardProps) {
  const accent = ACCENT_COLORS[accentColor];

  return (
    <button
      onClick={onClick}
      className="group flex-shrink-0 w-[180px] rounded-xl overflow-hidden transition-all duration-200 hover:scale-[1.02] text-left"
      style={{
        background: themeStyles.surface,
        border: `1px solid ${themeStyles.border}`,
      }}
      title={description}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accent.hoverBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = themeStyles.border;
      }}
    >
      {/* Thumbnail */}
      <div
        className="w-full h-[100px] flex items-center justify-center"
        style={{
          background: thumbnail ? `url(${thumbnail}) center/cover` : gradient,
        }}
      >
        {!thumbnail && (
          <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
            Preview
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Title */}
        <h4
          className="text-sm font-semibold truncate"
          style={{ color: themeStyles.text }}
        >
          {name}
        </h4>

        {/* Badge */}
        <span
          className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium"
          style={{
            background: accent.badgeBg,
            color: accent.badgeText,
          }}
        >
          {badge}
        </span>
      </div>
    </button>
  );
}
